import { Box, SideBar } from 'pixelize-design-library';
import { useCallback, useMemo, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from '@core/store/hooks';
import { preloadRoute } from '@modules/registry';
import { activeNavId, navItemsForRole } from '../navigation';

const HIDDEN_SCROLLBAR = {
  '#sidebar-scroll-container, #sidebar-scroll-container:hover': { scrollbarWidth: 'none' },
  '#sidebar-scroll-container::-webkit-scrollbar': { display: 'none' }
};

interface AppSidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  mobileNav?: boolean;
  onToggleMobileNav?: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

/** Derived entirely from `core/layout/navigation.ts` — never add a menu entry here. */
export function AppSidebar({
  mobileOpen,
  onMobileClose,
  mobileNav,
  onToggleMobileNav,
  collapsed,
  onToggleCollapsed
}: AppSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const role = useAppSelector((s) => s.auth.user?.role);

  const menus = useMemo(
    () =>
      navItemsForRole(role).map((item) => ({
        id: item.id,
        title: t(item.labelKey),
        url: item.path,
        icon: item.icon,
        group: t(item.groupKey)
      })),
    [role, t]
  );

  const activeMenu = useMemo(() => activeNavId(location.pathname, role), [location.pathname, role]);

  // The library renders every destination as a real anchor, submenu entries included.
  const prefetch = useCallback((event: SyntheticEvent) => {
    const link = (event.target as HTMLElement).closest?.('a[href]') as HTMLAnchorElement | null;
    if (link) preloadRoute(new URL(link.href, window.location.origin).pathname);
  }, []);

  const handleMenuClick = useCallback(
    (menu: { url?: string }) => {
      if (menu.url) navigate(menu.url);
    },
    [navigate]
  );

  return (
    <Box
      data-testid='app-sidebar'
      sx={HIDDEN_SCROLLBAR}
      onPointerOver={prefetch}
      onFocus={prefetch}
    >
      <SideBar
        isLoading={false}
        menus={menus}
        activeMenu={activeMenu}
        handleMenuClick={handleMenuClick}
        toggle={collapsed}
        changeToggle={onToggleCollapsed}
        logo=''
        companyName={t('nav.companyName')}
        isDisableAddNew
        selectedSecondaryItem={null}
        mobileOpen={mobileOpen}
        onMobileClose={onMobileClose}
        mobileNav={mobileNav}
        onToggleMobileNav={onToggleMobileNav}
      />
    </Box>
  );
}

export default AppSidebar;
